// eslint-disable-next-line @nx/enforce-module-boundaries

import { Injectable } from '@angular/core';
import { AllFlags, ErrorReporter, FeatureFlagsFacade, OrgTypes } from '@mode/shared/contract-common';
import { getLaunchDarklyClientId, getWebappRevision } from '@mode/shared/util-js';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';
import { LDClient, LDFlagSet, LDOptions, initialize } from 'launchdarkly-js-client-sdk';
import {
  AccountsNeededForInitialization,
  FeatureFlag,
  FeatureFlagsInitializationService,
  MigrationFlag,
} from 'libs/shared/contract-common/src/lib/flags.types';
import { first } from 'rxjs';

export interface FeatureFlagContext {
  billing_plan_code?: OrgTypes.PlanCode | 'user';
  email?: string;
  org_created_at?: number;
  organization?: string;
  orgName?: string; // orgName is an alias for organization
  username?: string;
}

@Injectable({ providedIn: 'root' })
export class LaunchDarklyFlagsDataService implements FeatureFlagsInitializationService {
  private client: LDClient | undefined;

  constructor(
    private window: Window,
    private errorReporter: ErrorReporter,
    public featureFlagsFacade: FeatureFlagsFacade,
    private observabilityService: ObservabilityService
  ) {}

  public async initFlags({ user, organization }: AccountsNeededForInitialization) {
    const sdkKey = getLaunchDarklyClientId(window.document);

    // Measuring this blocking serial call to api/account#show and LD flag initialization for future optimizations
    this.observabilityService.mark(ObservabilityEvents.INIT_FLAGS_START);

    if (sdkKey != null) {
      const context: FeatureFlagContext = {};

      if (user != null) {
        if (user.isAnonymous === false) {
          context.email = user.email;
        }
        context.username = user.username;
      }

      if (OrgTypes.isOrg(organization)) {
        context.billing_plan_code = organization.user === true ? 'user' : organization.planCode;
        context.orgName = organization.username;
        context.organization = organization.username;
        context.org_created_at = organization.createdAt;
      }

      await this.init(sdkKey, user?.username, context);
      await this.attachFlagsToWindow();
      this.observabilityService.recordSpanTiming(ObservabilityEvents.INIT_FLAGS, ObservabilityEvents.INIT_FLAGS_START);
    } else {
      throw new Error('Unable to get LD client key');
    }
  }

  public variation<T extends string | number | boolean | MigrationFlag>(flagKey: FeatureFlag, defaultValue: T): T {
    if (!this.client) {
      return defaultValue;
    }
    return this.client.variation(flagKey, defaultValue);
  }

  /**
   * Initialize the LD client, if not done previously, and retrieve all flags.
   * @param {string} sdkKey the LD client SDK key
   * @param {string} username username of the current user
   * @param {FeatureFlagContext} context
   */
  private async init(sdkKey: string, username: string | undefined, context: FeatureFlagContext): Promise<void> {
    if (this.client) return;

    try {
      const bootstrapFlagValues = this.getFlagsFromDocument(this.window.document);

      this.client = this.initLaunchDarklyClient(sdkKey, username, context, bootstrapFlagValues);

      const flags = await this.getAllFlags(this.client);

      this.featureFlagsFacade.init(flags as AllFlags);
    } catch (err: any) {
      this.errorReporter.notify({ error: err });
      // to prevent blocking the app, make all the flags false
      this.featureFlagsFacade.init({} as AllFlags);
    }
  }

  private initLaunchDarklyClient(
    sdkKey: string,
    username: string | undefined,
    context: FeatureFlagContext,
    bootstrap?: LDFlagSet
  ): LDClient {
    const options: LDOptions = {
      application: {
        id: 'webapp-ui',
        version: getWebappRevision(window.document) ?? 'unknown',
      },
      sendEvents: bootstrap?.['enable-client-side-launch-darkly-events'] ?? false,
      ...(bootstrap && { bootstrap }),
    };

    const client = initialize(
      sdkKey,
      {
        kind: 'user',
        key: username || 'anonymous-user',
        email: context?.email,
        anonymous: !username,
        ...(context?.billing_plan_code && { billing_plan_code: context.billing_plan_code }),
        ...(context?.organization && { organization: context.organization }),
        ...(context?.orgName && { orgName: context.orgName }),
        ...(context?.org_created_at && { org_created_at: context.org_created_at }),
      },
      options
    );

    return client;
  }

  private async getAllFlags(client: LDClient): Promise<LDFlagSet> {
    await client.waitUntilReady();
    return client.allFlags();
  }

  private async attachFlagsToWindow() {
    // attach feature flags to window for use during bootstrap (i.e. route configuration)
    const flags = await this.featureFlagsFacade.allFlags$.pipe(first()).toPromise();
    window['MODE_FLAGS'] = flags;
  }

  private getFlagsFromDocument(document: Document): object | undefined {
    const flagsContent = document.head.querySelector('#featureFlagState')?.textContent;
    return flagsContent ? JSON.parse(flagsContent) : undefined;
  }
}
